/**
 * Decodes a Base64 string and parses it as JSON.
 * @param value - The Base64 encoded string to decode and parse.
 * @return The parsed JSON object of type T, or null if the input is empty or decoding/parsing fails.
 */
export function parseBase64Json<T = unknown>(value?: string): T | null {
  if (!value) {
    return null;
  }

  try {
    const decodedValue = globalThis.atob(value);
    return JSON.parse(decodedValue) as T;
  } catch (err) {
    console.error("[parseBase64Json] Failed to decode or parse value:", err);
    return null;
  }
}

/**
 * Parses a string of key-value pairs (e.g. "key=value; key2=value2") into an object.
 * @param value - The raw string to parse.
 * @param separator - The delimiter between pairs (defaults to ";").
 * @return The parsed object containing key-value pairs, or an empty object if the input is null/undefined.
 */
export function parseKeyValueString<T = Record<string, string>>(
  value: string | null | undefined,
  separator: string = ";",
): T {
  if (!value) {
    return {} as T;
  }

  const result = value.split(separator).reduce(
    (acc, pair) => {
      const eqIndex = pair.indexOf("=");

      if (eqIndex > 0) {
        const key = pair.substring(0, eqIndex).trim();
        const value = pair.substring(eqIndex + 1).trim();

        if (key) {
          acc[key] = value;
        }
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return result as unknown as T;
}
